import React, { useContext } from "react";
import Tabs from "common/Tabs/Tabs";
import { ErrorText } from "features/bots/common/styles";
import {
  StrategyTabs,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import useStrategies, {
  UseStrategiesProps,
} from "@shared/features/bots/BotForm/Strategies/useStrategies";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BotType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import styled from "styled-components";
import { segmentedTabs } from "features/bots/BotForm/NewLook/styles";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle";
import { OrderTypes } from "@shared/features/trades/common/types";
import BuiltInStrategies from "features/bots/BotForm/Strategies/BuiltInStrategies";
import Strategy from "features/bots/BotForm/Strategies/Strategy";
import BotBaseStrategiesProvider from "@shared/features/bots/BotForm/Strategies/BotBaseStrategiesProvider";
import StrategyPrices from "features/bots/BotForm/Strategies/StrategyPrices";
import StrategyNumOrders from "features/bots/BotForm/Strategies/StrategyNumOrders";
import StrategyNumActiveOrders from "features/bots/BotForm/Strategies/StrategyNumActiveOrders";
import DefaultOrder from "features/bots/BotForm/common/DefaultOrder";
import useMeasure from "utils/hooks/useMeasure";
import OverrideToggle from "features/bots/BotForm/common/OverrideToggle";

const Strategies = ({ orderType }: UseStrategiesProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { botType, isChannelConfiguration } = useContext(BotFormContext)!;
  const {
    errorMessage,
    onTabChange,
    tab,
    showMultiToggleOverlay,
    getDefaultValue,
    ratiosToggleFieldData,
    firstTakeProfitDistanceData,
  } = useStrategies({ orderType });
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const isSignalsBot = botType === BotType.SIGNAL;
  const isHedgingBot = botType === BotType.HEDGING;
  const singleColumnLayout = width ? width < 500 : false;

  if (!tab) return null;

  const dcaStrategy = (
    <Strategy orderType={orderType} tab={StrategyTabs.DCA} selectedTab={tab} />
  );
  const builtInStrategy = (
    <BuiltInStrategies orderType={orderType} selectedTab={tab} />
  );
  let tabsItems = [
    {
      key: StrategyTabs.DCA,
      label: "DCA",
      children: dcaStrategy,
      forceRender: true,
    },
    {
      key: StrategyTabs.CUSTOM,
      label: t("strategy_tabs.custom"),
      children: (
        <Strategy
          orderType={orderType}
          tab={StrategyTabs.CUSTOM}
          selectedTab={tab}
        />
      ),
      forceRender: true,
    },
  ];
  if (isSignalsBot) {
    tabsItems.push({
      key: StrategyTabs.BUILT_IN,
      label: t("strategy_tabs.built_in"),
      children: builtInStrategy,
      forceRender: true,
    });
  }

  const getTabs = () => {
    if (botType === BotType.GRID || isHedgingBot) {
      return dcaStrategy;
    } else if (isSignalsBot) {
      tabsItems.reverse();
    }
    if (isChannelConfiguration) {
      tabsItems = tabsItems.slice(0, 2);
    }
    const tabsContent = (
      <StyledTabs activeKey={tab} onChange={onTabChange} items={tabsItems} />
    );
    if (ratiosToggleFieldData.isToggleField) {
      return (
        <TitleWithToggle
          content={
            <>
              {tabsContent}
              <OverrideToggle
                overrideField={ratiosToggleFieldData.overrideFieldType}
              />
            </>
          }
          fieldType={ratiosToggleFieldData.fieldType}
          title={ratiosToggleFieldData.title}
          overrideObjectName={ratiosToggleFieldData.overrideObjectName}
        />
      );
    }
    return tabsContent;
  };

  const getSignalsBotFirstTakeProfit = () => {
    if (!firstTakeProfitDistanceData.shouldDisplay) return;
    return (
      <div style={{ marginBottom: 16 }}>
        <TitleWithToggle
          title={firstTakeProfitDistanceData.title}
          content={
            <DefaultOrder
              orderType={OrderTypes.TAKE_PROFIT}
              hideBaseline={true}
              hidePercentTitle={true}
            />
          }
          fieldType={firstTakeProfitDistanceData.fieldType}
          getDefaultValue={firstTakeProfitDistanceData.getDefaultValue}
        />
      </div>
    );
  };

  let content = (
    <>
      {getTabs()}
      {isSignalsBot && !isChannelConfiguration && (
        <>
          <div style={{ marginTop: 16 }}>{getSignalsBotFirstTakeProfit()}</div>
          <StrategyPrices />
          <StrategyNumOrders />
          <StrategyNumActiveOrders />
        </>
      )}
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </>
  );

  if (showMultiToggleOverlay) {
    content = (
      <TitleWithToggle
        content={content}
        fieldType={
          orderType === OrderTypes.ENTRY
            ? ToggleField.ENTRIES
            : ToggleField.TAKE_PROFIT_STRATEGY
        }
        getDefaultValue={getDefaultValue}
      />
    );
  }

  return (
    <BotBaseStrategiesProvider
      orderType={orderType}
      singleColumnLayout={singleColumnLayout}
    >
      <div ref={measureRef} style={{ opacity: width ? 1 : 0 }}>
        {content}
      </div>
    </BotBaseStrategiesProvider>
  );
};

export default Strategies;

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab {
    padding: 0 0 12px 0;
  }
  ${(p) => p.theme.isNewLook && segmentedTabs};
`;
